// QueryField — the library's search field (library search 2026-10-01): the
// query text is the single source of truth, so the field is controlled by the
// host. Typing a modifier prefix offers modifiers (`au` -> author:); after
// `key:` it offers the values with counts (Slack's modifier autocomplete,
// Algolia facet counts). ↑↓ move, Enter/Tab fill in, Esc closes — keys live
// INSIDE the field only (no chords on web). With no suggestion open, the
// arrows and Enter go to the list (`onListKey`). Under the field, each filter
// the text holds is a chip that removes exactly its token.
//
// View-ephemeral state only: which suggestion is highlighted and whether the
// author closed the menu (both reset on the next keystroke).

import { useLingui } from "@lingui/react/macro";
import { type KeyboardEvent, type ReactNode, useState } from "react";
import { Icon } from "../Icon/Icon.js";
import { Text } from "../Text/Text.js";
import {
    accept,
    type ModifierKey,
    removeToken,
    type Suggestion,
    suggest,
    type Token,
} from "./libraryQuery.js";
import type { SearchResult } from "./searchModel.js";

// A key of the field's own result list (no suggestion open).
export type ListKey = "up" | "down" | "enter";

export const QueryField = ({
    query,
    onQueryChange,
    tokens,
    chipKeys,
    pool,
    activeOptionId,
    expanded,
    onListKey,
}: {
    readonly query: string;
    readonly onQueryChange: (query: string) => void;
    readonly tokens: readonly Token[];
    // The filter keys shown as chips (the tab's own token is the tab's).
    readonly chipKeys: readonly ModifierKey[];
    // The rows suggestions count values over.
    readonly pool: readonly SearchResult[];
    // The highlighted list row, for `aria-activedescendant`.
    readonly activeOptionId: string | undefined;
    readonly expanded: boolean;
    // A list key (↑ ↓ Enter) while no suggestion is open; true = handled.
    readonly onListKey: (key: ListKey) => boolean;
}): ReactNode => {
    const { t } = useLingui();
    const [active, setActive] = useState(0);
    const [closed, setClosed] = useState(false);
    const suggestions = closed ? [] : suggest(pool, query);
    const open = suggestions.length > 0;

    const change = (next: string): void => {
        setActive(0);
        setClosed(false);
        onQueryChange(next);
    };
    const choose = (s: Suggestion): void => change(accept(query, s));

    const hint = (key: ModifierKey): string => {
        switch (key) {
            case "type":
                return t`Source, material, package or file`;
            case "author":
                return t`Who wrote it`;
            case "created":
                return t`Year or month it was added`;
            case "status":
                return t`Archived items`;
        }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        if (open) {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const step = e.key === "ArrowDown" ? 1 : -1;
                setActive(
                    (i) => (i + step + suggestions.length) % suggestions.length,
                );
            } else if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                const pick =
                    suggestions[Math.min(active, suggestions.length - 1)];
                if (pick !== undefined) choose(pick);
            } else if (e.key === "Escape") {
                e.preventDefault();
                setClosed(true);
            }
            return;
        }
        const key: ListKey | undefined =
            e.key === "ArrowDown"
                ? "down"
                : e.key === "ArrowUp"
                  ? "up"
                  : e.key === "Enter"
                    ? "enter"
                    : undefined;
        if (key !== undefined && onListKey(key)) e.preventDefault();
    };

    const chips = tokens.flatMap((tok) =>
        (tok.term.type === "filter" || tok.term.type === "exclusion") &&
        chipKeys.includes(tok.term.key)
            ? [{ raw: tok.raw, term: tok.term }]
            : [],
    );

    return (
        <>
            <div className="bsx-search-field">
                <span className="bsx-search-icon" aria-hidden="true">
                    <Icon name="search" size="sm" />
                </span>
                <input
                    className="bsx-search-input"
                    type="text"
                    // biome-ignore lint/a11y/noAutofocus: a summoned finder takes focus by design (the Finder precedent)
                    autoFocus
                    role="combobox"
                    aria-label={t`Search your workspace`}
                    aria-expanded={open || expanded}
                    aria-controls={open ? "bsx-suggest" : "bsx-results"}
                    aria-activedescendant={
                        open ? `bsx-sug-${active}` : activeOptionId
                    }
                    aria-autocomplete="list"
                    placeholder={t`Search the materials`}
                    value={query}
                    onChange={(e) => change(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={() => setClosed(true)}
                />
                {open ? (
                    <div
                        id="bsx-suggest"
                        role="listbox"
                        aria-label={t`Suggestions`}
                        className="bsx-suggest"
                    >
                        {suggestions.map((s, i) => (
                            <div
                                key={`${s.type}-${s.key}-${s.type === "value" ? s.value : ""}`}
                                id={`bsx-sug-${i}`}
                                role="option"
                                tabIndex={-1}
                                aria-selected={i === active}
                                className={`bsx-suggest-row${i === active ? " bsx-suggest-row--active" : ""}`}
                                // mousedown, so the field keeps focus.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    choose(s);
                                }}
                            >
                                <code className="bsx-token">
                                    {`${s.negated ? "-" : ""}${s.key}:`}
                                </code>
                                {s.type === "modifier" ? (
                                    <Text as="span" size="sm" tone="dim">
                                        {hint(s.key)}
                                    </Text>
                                ) : (
                                    <>
                                        <Text as="span" size="sm">
                                            {s.value}
                                        </Text>
                                        <Text
                                            as="span"
                                            size="xs"
                                            tone="faint"
                                            className="bsx-suggest-end"
                                        >
                                            {s.count}
                                        </Text>
                                    </>
                                )}
                            </div>
                        ))}
                        <Text
                            as="div"
                            size="xs"
                            tone="faint"
                            className="bsx-suggest-foot"
                        >
                            {t`↑↓ choose · Enter or Tab fills in · Esc closes`}
                        </Text>
                    </div>
                ) : null}
            </div>
            {chips.length > 0 ? (
                <div className="bsx-chips">
                    {chips.map(({ raw, term }) => {
                        const token = term.key;
                        return (
                            <span
                                key={raw}
                                className={`bsx-chip${term.type === "exclusion" ? " bsx-chip--not" : ""}`}
                            >
                                <Text as="span" size="xs" tone="dim">
                                    {term.type === "exclusion"
                                        ? t`not ${token}`
                                        : token}
                                </Text>
                                <Text as="span" size="xs" weight="medium">
                                    {term.values.join(", ")}
                                </Text>
                                <button
                                    type="button"
                                    className="bsx-chip-x"
                                    aria-label={t`Remove ${raw}`}
                                    onClick={() =>
                                        change(removeToken(query, raw))
                                    }
                                >
                                    <Icon name="close" size="xs" />
                                </button>
                            </span>
                        );
                    })}
                </div>
            ) : null}
        </>
    );
};
